/**
 * Module dependencies.
 */

import { ComponentProps } from 'react';
import { Control, FieldValues, Path, UseControllerProps, useController } from 'react-hook-form';
import { assignRefs } from 'src/core/utils/refs';

import { RadioGroup, RadioGroupProps } from './index';

/**
 * `RadioGroupFieldProps` type.
 */

type RadioGroupFieldProps<T extends FieldValues = FieldValues> = {
  control: Control<T>;
  hideErrors?: boolean;
  ref?: ComponentProps<typeof RadioGroup>['ref'];
} & Omit<RadioGroupProps, 'onChange'> &
  Omit<UseControllerProps, 'control'>;

/**
 * Export `RadioGroupField` component.
 */

export function RadioGroupField<T extends FieldValues>(props: RadioGroupFieldProps<T>) {
  const { control, defaultValue, hideErrors, name, ref: forwardedRef, rules, ...rest } = props;
  const {
    field: { ref, ...fieldProps },
    fieldState: { error }
  } = useController({
    control,
    defaultValue,
    name: name as Path<T>,
    rules: rules as UseControllerProps<T>['rules']
  });

  return (
    <RadioGroup
      {...rest}
      {...fieldProps}
      error={hideErrors ? undefined : error?.message}
      name={name}
      ref={assignRefs(forwardedRef, ref)}
    />
  );
}
